// Zustand store for the results-column React island.
//
// Two concerns:
//   1. Engine data — populated by the Shiny -> React custom-message bridge
//      (shiny_bridge.ts) on every reactive recompute. Plumber still seeds
//      the initial baseline on mount.
//   2. View state — which dimension card is currently expanded, if any.
//      URL-synced via url_sync.ts so deep links and the back button work.
//
// The previous "summary vs explore" view modes collapsed into one piece
// of state — `expandedDimension`. Null means the 3x2 dashboard grid;
// a key means that card expands in-place to a full chart while the
// other five compress into a strip above it.

import { create } from "zustand";
import type { EngineOutput } from "../lib/types";
import type { DimensionKey } from "../lib/dimensions";

export interface ResultsState {
  // Engine data.
  engineOutput: EngineOutput | null;
  baselineOutput: EngineOutput | null;
  isModified: boolean;

  // View state.
  expandedDimension: DimensionKey | null;

  // Processing state. True between a Simulate click and the engine output
  // landing back from Shiny — drives the "Simulating…" overlay and the
  // Simulate button's busy state. R is single-threaded and blocks during
  // simulate_reform(), so this is toggled client-side (see
  // state/simulating_signal.ts) rather than by a server reactive.
  isSimulating: boolean;

  // Error state. Set when a reform fails (Shiny pushes "beamm:engine_error"),
  // cleared when the next successful output lands. Drives the error banner and
  // lets the failure dismiss the "Simulating…" overlay immediately instead of
  // waiting out the safety timeout. Null means no current error.
  engineError: string | null;

  // Mutators.
  setEngineOutput: (output: EngineOutput) => void;
  setBaselineOutput: (output: EngineOutput) => void;
  setIsModified: (modified: boolean) => void;
  setExpandedDimension: (dim: DimensionKey | null) => void;
  setSimulating: (simulating: boolean) => void;
  setEngineError: (message: string | null) => void;
  // Reset the results to the baseline (page-level "Reset all") — shows the
  // baseline immediately without waiting for a Simulate click, and returns
  // the dashboard to its grid "home" view by collapsing any expanded card.
  resetToBaseline: () => void;
}

export const useResultsStore = create<ResultsState>((set) => ({
  engineOutput: null,
  baselineOutput: null,
  isModified: false,
  expandedDimension: null,
  isSimulating: false,
  engineError: null,

  // A successful output supersedes any prior error, so clear it here.
  setEngineOutput: (output) => set({ engineOutput: output, engineError: null }),
  setBaselineOutput: (output) => set({ baselineOutput: output }),
  setIsModified: (modified) => set({ isModified: modified }),
  setExpandedDimension: (dim) => set({ expandedDimension: dim }),
  setSimulating: (simulating) => set({ isSimulating: simulating }),
  setEngineError: (message) => set({ engineError: message }),
  resetToBaseline: () =>
    set((s) => ({
      engineOutput: s.baselineOutput,
      isModified: false,
      expandedDimension: null,
      engineError: null,
    })),
}));
